import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Link,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { bankAccountService } from '../services/bankAccountService';

const RegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [banks, setBanks] = useState([]);
  const [resolvingAccount, setResolvingAccount] = useState(false);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const steps = ['Account Type', 'Basic Info', 'Profile Details'];

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      userType: ''
    }
  });

  const userType = watch('userType');
  const riderAccountNumber = watch('riderAccountNumber');
  const riderBankCode = watch('riderBankCode');

  useEffect(() => {
    if (userType === 'rider') {
      const loadBanks = async () => {
        try {
          const banksData = await bankAccountService.getBanks();
          setBanks(banksData || []);
        } catch (err) {
          console.error('Failed to load banks:', err);
        }
      };
      loadBanks();
    }
  }, [userType]);

  useEffect(() => {
    if (userType === 'rider' && riderAccountNumber && riderBankCode && riderAccountNumber.length === 10) {
      setResolvingAccount(true);
      bankAccountService.resolveAccount(riderAccountNumber, riderBankCode)
        .then(resolved => {
          setValue('riderAccountName', resolved.accountName);
        })
        .catch(err => {
          console.error('Failed to resolve account:', err);
        })
        .finally(() => {
          setResolvingAccount(false);
        });
    }
  }, [riderAccountNumber, riderBankCode, userType, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // Format data for backend API
      const registrationData = { ...data };
      
      // Remove confirmPassword as it's not needed by the backend
      delete registrationData.confirmPassword;
      
      // Helper function to remove empty strings and undefined values
      const removeEmptyFields = (obj) => {
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== '' && value !== undefined && value !== null) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              const cleanedNested = removeEmptyFields(value);
              if (Object.keys(cleanedNested).length > 0) {
                cleaned[key] = cleanedNested;
              }
            } else {
              cleaned[key] = value;
            }
          }
        }
        return cleaned;
      };
      
      // Format pharmacy data if userType is pharmacy
      if (data.userType === 'pharmacy') {
        // Only include pharmacy-specific fields if they have values
        if (!data.pharmacyName) delete registrationData.pharmacyName;
        if (!data.licenseNumber) delete registrationData.licenseNumber;
        
        // Format contactInfo only if phone is provided
        if (data.phone) {
          registrationData.contactInfo = {
            phone: data.phone,
            email: data.email || undefined
          };
          delete registrationData.phone; // Remove flat phone field
        } else {
          delete registrationData.phone;
        }
      }

      // Format rider data if userType is rider
      if (data.userType === 'rider') {
        // Format vehicleInfo
        if (data.vehicleInfo) {
          registrationData.vehicleInfo = {
            type: data.vehicleInfo.type
          };
        }

        // Bank account will be set up after registration via bank account endpoint
        // We don't include it in initial registration to keep it simple
        delete registrationData.riderAccountNumber;
        delete registrationData.riderBankCode;
        delete registrationData.riderAccountName;
      }
      
      // Format address if provided (only include non-empty fields)
      if (data.address) {
        const addressFields = {};
        if (data.address.city) addressFields.city = data.address.city;
        if (data.address.state) addressFields.state = data.address.state;
        if (Object.keys(addressFields).length > 0) {
          addressFields.country = 'Nigeria';
          registrationData.address = addressFields;
        } else {
          delete registrationData.address;
        }
      }
      
      // Remove any remaining empty fields
      const cleanedData = removeEmptyFields(registrationData);
      
      const result = await registerUser(cleanedData);
      
      if (result.success) {
        // If rider provided bank account details, set them up
        if (data.userType === 'rider' && data.riderAccountNumber && data.riderBankCode) {
          try {
            await bankAccountService.updateRiderBankAccount({
              accountNumber: data.riderAccountNumber,
              bankCode: data.riderBankCode,
              accountName: data.riderAccountName,
              bankName: banks.find(b => b.code === data.riderBankCode)?.name || ''
            });
          } catch (bankErr) {
            console.error('Failed to set up bank account:', bankErr);
            // Don't fail registration, bank account can be added later
          }
        }

        if (result.requiresProfileSetup) {
          // Redirect to pharmacy setup page
          navigate('/pharmacy-setup');
        } else {
          // Go to dashboard
          navigate('/dashboard');
        }
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          (err.response?.data?.errors && Array.isArray(err.response.data.errors) 
                            ? err.response.data.errors.map(e => e.msg || e.message).join(', ')
                            : null) ||
                          'Registration failed. Please try again.';
      setError(errorMessage);
    }
    
    setLoading(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleNext = async () => {
    setError(''); // Clear any previous errors
    
    // Validate current step before moving forward (for all steps except the last one)
    if (activeStep === 0) {
      // Step 0: Validate account type selection for all user types (pharmacy, customer, rider)
      const isValid = await trigger('userType');
      if (!isValid || !userType) {
        setError('Please select an account type');
        return;
      }
    } else if (activeStep === 1) {
      // Step 1: Validate basic info fields for all user types (pharmacy, customer, rider)
      const fieldsToValidate = ['email', 'password', 'confirmPassword'];
      const isValid = await trigger(fieldsToValidate);
      
      // Get current values using getValues for more reliable access
      const formValues = getValues();
      const email = formValues.email;
      const password = formValues.password;
      const confirmPassword = formValues.confirmPassword;
      
      // Check if all required fields are filled
      if (!email || !password || !confirmPassword) {
        setError('Please fill in all required fields');
        return;
      }
      
      // Check validation results - trigger returns false if any field fails validation
      if (!isValid) {
        // Get the first error message from the errors object
        // Note: errors object is updated synchronously after trigger
        const firstError = 
          errors.email?.message ||
          errors.password?.message ||
          errors.confirmPassword?.message ||
          'Please fill in all required fields correctly';
        setError(firstError);
        return;
      }
      
      // Additional validation for password match (this is also checked by react-hook-form, but double-check)
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }
    // Note: Step 2 (last step) doesn't need validation here as form submission handles it via handleSubmit
    
    // If validation passes, move to next step
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError(''); // Clear errors when going back
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Account Type</InputLabel>
              <Select
                {...register('userType', { required: 'Please select an account type' })}
                error={!!errors.userType}
                label="Account Type"
              >
                <MenuItem value="pharmacy">Pharmacy</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="rider">Delivery Rider</MenuItem>
              </Select>
            </FormControl>
            {errors.userType && (
              <Typography variant="caption" color="error">
                {errors.userType.message}
              </Typography>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters'
                }
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              margin="normal"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: value => value === watch('password') || 'Passwords do not match'
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            {userType === 'pharmacy' && (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  You can complete your pharmacy profile details after registration, or fill them out now for immediate setup.
                </Alert>
                <TextField
                  fullWidth
                  label="Pharmacy Name"
                  margin="normal"
                  {...register('pharmacyName')}
                  error={!!errors.pharmacyName}
                  helperText={errors.pharmacyName?.message || 'Leave blank to complete later'}
                />
                <TextField
                  fullWidth
                  label="License Number"
                  margin="normal"
                  {...register('licenseNumber')}
                  error={!!errors.licenseNumber}
                  helperText={errors.licenseNumber?.message || 'Leave blank to complete later'}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  margin="normal"
                  {...register('phone')}
                  error={!!errors.phone}
                      helperText={errors.phone?.message || 'Leave blank to complete later'}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="City"
                      margin="normal"
                      {...register('address.city')}
                      error={!!errors.address?.city}
                      helperText={errors.address?.city?.message || 'Leave blank to complete later'}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="State"
                      margin="normal"
                      {...register('address.state')}
                      error={!!errors.address?.state}
                      helperText={errors.address?.state?.message || 'Leave blank to complete later'}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {userType === 'customer' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      margin="normal"
                      {...register('firstName', { required: 'First name is required' })}
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      margin="normal"
                      {...register('lastName', { required: 'Last name is required' })}
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Phone Number"
                  margin="normal"
                  {...register('phone', { required: 'Phone number is required' })}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="City"
                      margin="normal"
                      {...register('address.city', { required: 'City is required' })}
                      error={!!errors.address?.city}
                      helperText={errors.address?.city?.message}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="State"
                      margin="normal"
                      {...register('address.state', { required: 'State is required' })}
                      error={!!errors.address?.state}
                      helperText={errors.address?.state?.message}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Referral Code (Optional)"
                  margin="normal"
                  {...register('referralCode')}
                  error={!!errors.referralCode}
                  helperText={errors.referralCode?.message || 'Enter a friend\'s referral code to earn rewards'}
                  placeholder="Enter referral code"
                />
              </>
            )}

            {userType === 'rider' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      margin="normal"
                      {...register('firstName', { required: 'First name is required' })}
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      margin="normal"
                      {...register('lastName', { required: 'Last name is required' })}
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Phone Number"
                  margin="normal"
                  {...register('phone', { required: 'Phone number is required' })}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Vehicle Type</InputLabel>
                  <Select
                    {...register('vehicleInfo.type', { required: 'Vehicle type is required' })}
                    error={!!errors.vehicleInfo?.type}
                    label="Vehicle Type"
                  >
                    <MenuItem value="bike">Bike</MenuItem>
                    <MenuItem value="motorcycle">Motorcycle</MenuItem>
                    <MenuItem value="car">Car</MenuItem>
                    <MenuItem value="bicycle">Bicycle</MenuItem>
                  </Select>
                </FormControl>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="City"
                      margin="normal"
                      {...register('address.city', { required: 'City is required' })}
                      error={!!errors.address?.city}
                      helperText={errors.address?.city?.message}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="State"
                      margin="normal"
                      {...register('address.state', { required: 'State is required' })}
                      error={!!errors.address?.state}
                      helperText={errors.address?.state?.message}
                    />
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Bank Account Details (for receiving payments)
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add your bank account to receive delivery fees. Your account will be verified automatically.
                </Alert>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Select Bank</InputLabel>
                  <Select
                    {...register('riderBankCode', { required: 'Please select a bank' })}
                    label="Select Bank"
                    error={!!errors.riderBankCode}
                  >
                    {banks.map((bank) => (
                      <MenuItem key={bank.code} value={bank.code}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.riderBankCode && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.riderBankCode.message}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label="Account Number"
                  margin="normal"
                  {...register('riderAccountNumber', {
                    required: 'Account number is required',
                    pattern: {
                      value: /^\d{10}$/,
                      message: 'Account number must be 10 digits'
                    }
                  })}
                  error={!!errors.riderAccountNumber}
                  helperText={errors.riderAccountNumber?.message || 'Enter your 10-digit account number'}
                />
                <TextField
                  fullWidth
                  label="Account Name"
                  margin="normal"
                  {...register('riderAccountName')}
                  error={!!errors.riderAccountName}
                  helperText={errors.riderAccountName?.message || (resolvingAccount ? 'Resolving account name...' : 'Account name will be resolved automatically')}
                  disabled={resolvingAccount}
                />
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1548A6 0%, #64B5F6 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Card elevation={8}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box component="img" src="/pd-logo.png" alt="Prescription Drives" sx={{ height: 56, mb: 1 }} />
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary.main">
                Join PD App
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create your account and start using our platform
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              {renderStepContent(activeStep)}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  type="button"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Box>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{ ml: 1 }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleNext}
                      disabled={!userType && activeStep === 0}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2">
                  Already have an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/login')}
                    sx={{ textDecoration: 'none' }}
                  >
                    Sign in here
                  </Link>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default RegisterPage;
